<template>
    <v-dialog :model-value="modelValue" @update:modelValue="$emit('update:modelValue', $event)" class="pa-5"
        max-width="600px">
        <v-card class="pa-2">
            <v-card-title class="text-h6">Edit Shop Information</v-card-title>
            <v-card-text>
                <v-form ref="form" v-model="formValid">
                    <!-- Shop Image Section -->
                    <div class="shop-image-section mb-4">
                        <div class="image-label">Shop Logo</div>
                        
                        <!-- Image Preview -->
                        <div class="image-preview-container">
                            <div class="current-image" v-if="imagePreview || shopImageUrl">
                                <img :src="imagePreview || shopImageUrl" alt="Shop preview" class="preview-img" 
                                    @click="triggerFileUpload" />
                                <div class="image-overlay" @click="triggerFileUpload">
                                    <v-icon size="30" color="white">mdi-camera</v-icon>
                                    <span class="overlay-text">Change Image</span>
                                </div>
                            </div>
                            <div v-else class="empty-image" @click="triggerFileUpload">
                                <v-icon size="50" color="grey">mdi-image-plus</v-icon>
                                <span class="empty-text text-center">Click to upload shop logo</span>
                            </div>
                        </div>
                        
                        <input type="file" 
                            ref="fileInput"
                            @change="handleFileUpload"
                            accept="image/jpeg,image/png,image/jpg,image/webp" 
                            style="display: none" />
                        
                        <!-- Upload Progress -->
                        <v-progress-linear v-if="uploading" indeterminate color="primary" class="mt-2" />
                        
                        <!-- Image Info -->
                        <div v-if="shop.has_image && !imagePreview && !removeCurrentImage" class="image-info-text">
                            <v-icon size="small" color="success">mdi-check-circle</v-icon>
                            <span class="text-caption">Current logo: {{ shop.image_size_kb }}KB (Tap image to change)</span>
                        </div>
                        <div v-else-if="imagePreview" class="image-info-text">
                            <v-icon size="small" color="info">mdi-information</v-icon>
                            <span class="text-caption">New image selected - will be optimized on save</span>
                        </div>
                        <div v-else-if="removeCurrentImage" class="image-info-text">
                            <v-icon size="small" color="warning">mdi-alert</v-icon>
                            <span class="text-caption">Current image will be removed</span>
                        </div>
                    </div>

                    <v-row class="mb-1 pa-2">
                        <v-col cols="12" md="6" style="padding: 3px !important;">
                            <v-text-field 
                                v-model="localShop.shop_owner"
                                label="Owner's Full Name"
                                :rules="[requiredRule]"
                                class="custom-input"
                                variant="outlined"
                                density="compact" />
                        </v-col>

                        <v-col cols="12" md="6" style="padding: 3px !important;">
                            <v-text-field 
                                v-model="localShop.shop_name"
                                label="Store / Business Name"
                                :rules="[requiredRule, max50Rule]"
                                class="custom-input"
                                variant="outlined"
                                density="compact"
                                maxlength="50"
                                counter="50" />
                        </v-col>
                        
                        <v-col cols="12" md="6" style="padding: 3px !important;">
                            <v-select 
                                v-model="localShop.shop_type"
                                label="Store Type"
                                :items="storeTypes"
                                class="custom-input"
                                variant="outlined"
                                density="compact" />
                        </v-col>
                        
                        <v-col cols="12" md="6" style="padding: 3px !important;">
                            <v-text-field 
                                v-model="localShop.shop_email"
                                label="Email Address"
                                type="email"
                                :rules="[requiredRule, emailFormatRule]"
                                class="custom-input"
                                variant="outlined"
                                density="compact" />
                        </v-col>
                        
                        <v-col cols="12" md="6" style="padding: 3px !important;">
                            <v-text-field 
                                v-model="localShop.shop_contact_number"
                                label="Mobile Number"
                                :rules="[requiredRule, mobileNumberRule]"
                                class="custom-input"
                                variant="outlined"
                                density="compact"
                                maxlength="13"
                                counter="13" />
                        </v-col>
                    </v-row>

                    <span style="font-size: small">
                        Last changes: {{ formatDate(shop.updated_at) }}
                    </span>
                </v-form>
            </v-card-text>
            
            <v-card-actions class="mx-3">
                <v-btn color="green" class="ms-3 mb-2" variant="flat" @click="confirmUpdate"
                    :disabled="!formValid || uploading || saving">
                    <v-icon>mdi-content-save</v-icon>&nbsp; Save
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn color="red" class="me-3 mb-2" variant="flat" @click="closeDialog" :disabled="saving">
                    <v-icon>mdi-close</v-icon>&nbsp; Close
                </v-btn>
            </v-card-actions>

            <!-- Confirmation Dialog -->
            <v-dialog :model-value="showConfirmDialog" @update:modelValue="showConfirmDialog = $event" max-width="600px"
                persistent>
                <v-card>
                    <v-card-title class="text-h6">Confirm Update</v-card-title>
                    <v-card-text>
                        Are you sure you want to save changes to <strong>{{ localShop.shop_name || shop.shop_name }}</strong>?
                        <div v-if="hasChanges" class="mt-2 text-caption">
                            <strong>Changes to be made:</strong>
                            <ul class="mt-1">
                                <li v-for="(change, field) in changesList" :key="field">
                                    {{ change.label }}: {{ change.from }} → {{ change.to }}
                                </li>
                            </ul>
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn color="red" variant="flat" class="me-2 mb-2" :disabled="saving"
                            @click="showConfirmDialog = false">
                            <v-icon>mdi-cancel</v-icon>&nbsp; Cancel
                        </v-btn>
                        <v-btn color="green" variant="flat" class="me-2 mb-2" :disabled="saving"
                            @click="updateShop">
                            <v-icon>mdi-check</v-icon>&nbsp; Confirm
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            
            <LoaderUI :visible="saving" message="Saving shop information..." />

            <!-- Shop Location Dialog -->
            <LocationConfirmDialog v-model="locationConfirmDialog" 
                @location-selected="handleLocationSelected" />
        </v-card>
    </v-dialog>
</template>

<script>
import LoaderUI from '@/components/LoaderUI.vue';
import LocationConfirmDialog from '@/components/Dialogs/LocationConfirmDialog.vue'
import { useToast } from 'vue-toastification';
import { useAuthStore } from '@/stores/auth';
import { useShopStore } from '@/stores/shopStore';

export default {
    name: 'ShopEditDialog',
    components: {
        LoaderUI,
        LocationConfirmDialog,
    },

    props: {
        modelValue: {
            type: Boolean,
            required: true
        },
        shop: {
            type: Object,
            default: () => ({})
        },
    },

    emits: [
        'update:modelValue',
        'update:success',
        'update:error'
    ],

    setup() {
        const toast = useToast();
        const authStore = useAuthStore();
        const shopStore = useShopStore();
        return { toast, authStore, shopStore };
    },

    data() {
        return {
            localShop: {},
            formValid: false,
            imagePreview: null,
            newImageFile: null,
            removeCurrentImage: false,
            uploading: false,
            saving: false,
            showConfirmDialog: false,
            locationConfirmDialog: false,
            storeTypes: [
                'Restaurant', 'Coffee Shop', 'Cafe', 'Carinderia',
                'Bakery', 'Street Food', 'Dessert Shop', 'Food Stall',
                'Other'
            ],
        }
    },

    computed: {
        shopImageUrl() {
            if (this.newImageFile) return null;
            if (this.removeCurrentImage) return null;
            if (this.shop?.has_image && this.shop?.thumbnail_url) {
                return this.shop.thumbnail_url;
            }
            return null;
        },

        hasChanges() {
            if (!this.shop || !this.localShop) return false;

            const fields = [
                'shop_owner', 'shop_name', 'shop_type', 'shop_email', 'shop_contact_number'
            ];
            
            for (const field of fields) {
                if (this.shop[field] != this.localShop[field]) {
                    return true;
                }
            }
            
            if (this.newImageFile || this.removeCurrentImage) {
                return true;
            }
            
            return false;
        },

        changesList() {
            if (!this.hasChanges) return {};
            
            const changes = {};
            const fields = ['shop_owner', 'shop_name', 'shop_type', 'shop_email', 'shop_contact_number'];
            const fieldLabels = {
                shop_owner: 'Shop Owner',
                shop_name: 'Shop Name',
                shop_type: 'Shop Type',
                shop_email: 'Email',
                shop_contact_number: 'Contact Number',
            };
            
            for (const field of fields) {
                const oldValue = this.shop[field];
                const newValue = this.localShop[field];
                
                if (oldValue != newValue) {
                    changes[field] = {
                        label: fieldLabels[field],
                        from: oldValue || 'Not set',
                        to: newValue || 'Not set'
                    };
                }
            }
            
            if (this.newImageFile) {
                changes['image'] = {
                    label: 'Shop Logo',
                    from: this.shop.has_image ? 'Has image' : 'No image',
                    to: 'New image uploaded'
                };
            } else if (this.removeCurrentImage) {
                changes['image'] = {
                    label: 'Shop Logo',
                    from: 'Has image',
                    to: 'Image removed'
                };
            }
            
            return changes;
        }
    },

    watch: {
        modelValue(newVal) {
            if (newVal && this.shop) {
                this.resetState();
                this.localShop = JSON.parse(JSON.stringify(this.shop));
            }
        },
        shop: {
            handler(newVal) {
                if (newVal && this.modelValue) {
                    this.localShop = JSON.parse(JSON.stringify(newVal));
                    this.resetImageState();
                }
            },
            deep: true,
            immediate: true
        },
    },

    methods: {
        resetState() {
            this.resetImageState();
            this.showConfirmDialog = false;
            this.saving = false;
            this.uploading = false;
        },

        triggerFileUpload() {
            this.$refs.fileInput.click();
        },

        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (file.size > 2 * 1024 * 1024) {
                this.toast.error('Image must be less than 2MB');
                return;
            }

            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                this.toast.error('Only JPG, PNG, or WebP images are allowed');
                return;
            }

            this.newImageFile = file;
            this.removeCurrentImage = false;

            const reader = new FileReader();
            reader.onload = (e) => {
                this.imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },

        resetImageState() {
            this.newImageFile = null;
            this.imagePreview = null;
            this.removeCurrentImage = false;
            this.uploading = false;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        requiredRule(v) {
            return !!v || 'This field is required';
        },

        max50Rule(v) {
            return (v && v.length <= 50) || 'Maximum 50 characters only';
        },

        emailFormatRule(v) {
            const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return pattern.test(v) || 'Please enter a valid email address';
        },

        mobileNumberRule(v) {
            const pattern = /^(\+63|0)[0-9]{10}$/;
            return pattern.test(v.replace(/\s/g, '')) || 'Enter a valid Philippine mobile number (e.g., 09123456789 or +639123456789)';
        },

        handleLocationSelected(locationData) {
            if (locationData && locationData.address) {
                const currentOpenAt = this.localShop.open_at;
                const currentCloseAt = this.localShop.close_at;
                
                this.localShop = {
                    ...this.localShop,
                    shop_address: locationData.address,
                    latitude: locationData.coordinates?.lat,
                    longitude: locationData.coordinates?.lng,
                    open_at: currentOpenAt,
                    close_at: currentCloseAt
                };
                
                this.toast.success('Location address has been set!');
            }
        },

        showLocationConfirmDialog() {
            this.locationConfirmDialog = true;
        },

        confirmUpdate() {
            if (!this.hasChanges) {
                this.toast.info('No changes to save');
                this.closeDialog();
                return;
            }
            
            this.showConfirmDialog = true;
        },

        async updateShop() {
            this.saving = true;
            this.showConfirmDialog = false;
            
            try {
                let formData;
                // let hasImageChange = false;
                const shopId = this.authStore.getShopId;
                const adminId = this.authStore.adminId;
                
                // Check if there's an image to upload
                if (this.newImageFile) {
                    // Use FormData for image upload
                    formData = new FormData();
                    formData.append('shop_id', shopId);
                    formData.append('admin_id', adminId);
                    formData.append('shop_owner', this.localShop.shop_owner?.trim() || '');
                    formData.append('shop_name', this.localShop.shop_name?.trim() || '');
                    formData.append('shop_type', this.localShop.shop_type?.trim() || '');
                    formData.append('shop_email', this.localShop.shop_email?.trim() || '');
                    formData.append('shop_contact_number', this.localShop.shop_contact_number?.trim() || '');
                    formData.append('image', this.newImageFile);
                    // hasImageChange = true;
                } 
                else if (this.removeCurrentImage) {
                    // Handle image removal
                    formData = new FormData();
                    formData.append('shop_id', shopId);
                    formData.append('admin_id', adminId);
                    formData.append('shop_owner', this.localShop.shop_owner?.trim() || '');
                    formData.append('shop_name', this.localShop.shop_name?.trim() || '');
                    formData.append('shop_type', this.localShop.shop_type?.trim() || '');
                    formData.append('shop_email', this.localShop.shop_email?.trim() || '');
                    formData.append('shop_contact_number', this.localShop.shop_contact_number?.trim() || '');
                    formData.append('remove_image', 'true');
                    // hasImageChange = true;
                }
                else {
                    // Use regular JSON for no image change
                    formData = {
                        shop_id: Number(shopId),
                        admin_id: Number(adminId),
                        shop_owner: this.localShop.shop_owner?.trim() || '',
                        shop_name: this.localShop.shop_name?.trim() || '',
                        shop_type: this.localShop.shop_type?.trim() || '',
                        shop_email: this.localShop.shop_email?.trim() || '',
                        shop_contact_number: this.localShop.shop_contact_number?.trim() || '',
                    };
                }
                
                // Call the API through shopStore
                const response = await this.shopStore.updateShopStore(formData);
                
                if (response && response.success) {
                    this.toast.success(response.message);
                    
                    // Emit success event to parent
                    this.$emit('update:success', response.data);
                    
                    // Close dialog after success
                    this.closeDialog();

                    // Update auth store shop image if changed
                    if (response.data.thumbnail_url !== this.authStore.shopImage) {
                        this.authStore.setShopImage(response.data.thumbnail_url);
                    }

                } else {
                    throw new Error(response?.message || 'Failed to update shop');
                }
                
            } catch (error) {
                console.error('Update shop error:', error);
                this.toast.error(error.message || "Failed to update shop");
                this.$emit('update:error', error.message);
            } finally {
                this.saving = false;
            }
        },

        closeDialog() {
            this.resetState();
            this.$emit('update:modelValue', false);
        },

        formatDate(dateString) {
            if (!dateString) return 'Invalid date';
            const date = new Date(dateString);
            return date.toLocaleString('en-PH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
                timeZone: 'Asia/Manila'
            });
        },
    }
}
</script>

<style scoped>
.v-icon {
    color: #fff;
}

.shop-image-section {
    margin-bottom: 16px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #999;
}

.image-label {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
}

.image-preview-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 150px;
}

.current-image {
    position: relative;
    display: inline-block;
    cursor: pointer;
}

.preview-img {
    width: 150px;
    height: 150px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #ddd;
    transition: opacity 0.3s ease;
}

.current-image:hover .preview-img {
    opacity: 0.7;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 7px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
}

.current-image:hover .image-overlay {
    opacity: 1;
}

.overlay-text {
    color: white;
    font-size: 12px;
    margin-top: 5px;
}

.empty-image {
    width: 170px;
    height: 170px;
    padding: 10px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.3s ease;
}

.empty-image:hover {
    border-color: #5c3a21;
    background: rgba(92, 58, 33, 0.05);
}

.empty-text {
    font-size: 12px;
    color: #999;
    margin-top: 8px;
}

.image-info-text {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 8px;
}

.text-caption {
    font-size: 11px;
    margin: 2px 0;
    color: #999;
}

.custom-input :deep(.v-field) {
    border-radius: 12px;
    transition: all 0.2s ease;
    margin-top: 7px;
}

.custom-input :deep(.v-field:hover) {
    border-color: #5c3a21;
}

.custom-input :deep(.v-field--focused) {
    border-color: #5c3a21;
    box-shadow: 0 0 0 2px rgba(182, 112, 0, 0.15);
}
</style>